import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Skeleton } from 'antd';
import DateRangePicker from 'components/DateRangePicker';
import { getDefaultDateRange } from 'shared/constants/date';
import { PlusOutlined } from '@ant-design/icons';
import useDataSource from 'hooks/useDataSource';

import SortableList from 'components/SortableList';
import DataSourceSelect from 'components/DataSourceSelect';
import { SEGMENT_COLORS, STATIC_SEGMENT_COLOR } from 'shared/constants/colors';
import EditSegmentDrawer from './EditSegmentDrawer';
import './global-filter.less';
import FilterSegment from './FilterSegment';
import AddSegmentDrawer from './AddSegmentDrawer';
import WebsiteUrlSelector from './WebsiteUrlSelector';

export const getFilter = filter => ({
  title: filter?.title,
  dateRangeConfig: filter?.dateRangeConfig || getDefaultDateRange({ compareToPrevious: true }),
  allEnabled: filter?.allEnabled || true,
  dataSourceIds: filter?.dataSources?.map(ds => ds.id) || [],
  segments: filter?.segments || [],
  websiteBaseUrls: filter?.websiteBaseUrls || [],
});

export const DEFAULT_FILTER = getFilter({});

const FilterForm = ({ onChange, filter, websites }) => {
  const { dataSourceId } = useDataSource();
  const [addSegment, setAddSegment] = useState(null);
  const [editSegment, setEditSegment] = useState(null);
  const [form] = Form.useForm();

  const filterFormValues = useMemo(() => getFilter(filter), [
    filter?.id,
    filter?.title,
    filter?.allEnabled,
    filter?.dateRangeConfig,
    filter?.segments,
    filter?.websiteBaseUrls,
    filter?.dataSources,
  ]);

  useEffect(() => {
    form.setFieldsValue(filterFormValues);
  }, [filterFormValues, form]);

  const allSegment = {
    id: 'all',
    title: 'All',
    color: STATIC_SEGMENT_COLOR.dark,
    enabled: filter?.allEnabled,
  };
  const mainSegment = allSegment.enabled ? allSegment : filter?.segments?.find(s => s.enabled);

  const onFilterChange = async (changedValues, allValues) => {
    if (changedValues.segments || changedValues.allEnabled || changedValues.dateRangeConfig || changedValues.dataSourceIds || changedValues.websiteBaseUrls) {
      onChange?.(allValues);
    }
  };

  const onAddSegment = (values) => {
    const sortIndex = filter?.segments?.length || 0;
    setAddSegment(false);
    if (values === 'custom') {
      setEditSegment({ sortIndex, color: SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0] });
    } else if (values === 'custom-date-range') {
      setEditSegment({ sortIndex, color: SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0], dateRangeConfig: getDefaultDateRange() });
    } else {
      onChange?.({ segments: [...(filter?.segments || []), { ...values, sortIndex, color: SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0] }] });
    }
  };

  const onSaveSegment = (values) => onChange?.({ segments: [...(filter?.segments?.filter(fs => fs.id !== values.id) || []), values] });

  const onRemoveSegment = () => onChange?.({ segments: filter?.segments?.filter(fs => fs.id !== editSegment.id) });

  if (!filter) {
    return <Skeleton active />;
  }

  return (
    <>
      <Form layout="vertical" onValuesChange={onFilterChange} form={form}>
        {!dataSourceId && (
          <Form.Item name="dataSourceIds" label="Data sources">
            <DataSourceSelect mode="multiple" allowClear />
          </Form.Item>
        )}
        <WebsiteUrlSelector websites={websites} />
        <Form.Item name="dateRangeConfig" label="Date range">
          <DateRangePicker defaultValue={getDefaultDateRange({ compareToPrevious: true })} />
        </Form.Item>
        <Form.Item label="Segments">
          <FilterSegment
            mainSegment={mainSegment}
            value={allSegment}
            onChange={({ enabled }) => onChange?.({ allEnabled: enabled })}
          />
          <Form.List name="segments">
            {(fields, { move }) => (
              <>
                <SortableList
                  onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
                  lockAxis="y"
                >
                  {fields.map((field, sortIndex) => (
                    <SortableList.Item key={field.key} index={sortIndex}>
                      <Form.Item {...field} noStyle>
                        <FilterSegment onClick={setEditSegment} mainSegment={mainSegment} sortable />
                      </Form.Item>
                    </SortableList.Item>
                  ))}
                </SortableList>

                <Button block onClick={() => setAddSegment(true)} icon={<PlusOutlined />}>Add Segment</Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
      <AddSegmentDrawer open={!!addSegment} segment={addSegment} onSubmit={onAddSegment} onCancel={() => setAddSegment(null)} globalDateRange={filter.dateRangeConfig} />
      <EditSegmentDrawer open={!!editSegment} segment={editSegment} onSubmit={onSaveSegment} onRemove={editSegment?.id !== filter?.mainSegment?.id && onRemoveSegment} onCancel={() => setEditSegment(null)} globalDateRange={filter.dateRangeConfig} />
    </>
  );
};

export default FilterForm;
